import type {
  Excursao,
  ReservaGrupoExcursao,
  MotivoCancelamentoExcursao,
} from '../../../shared/lib/excursaoStorage';
import type { ClienteRegistro } from './clientesStorage';
import { findClienteByEmail } from './clientesStorage';

export function formatBRL(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function parseValorGuiaPorInscrito(exc: Excursao): number {
  if (exc.valorGuia != null && Number.isFinite(exc.valorGuia)) return Math.max(0, exc.valorGuia);
  try {
    const s = exc.subtitle?.trim();
    if (s?.startsWith('{')) {
      const j = JSON.parse(s) as { valorGuia?: number };
      return Math.max(0, Number(j.valorGuia) || 0);
    }
  } catch {
    /* ignore */
  }
  return 0;
}

/** Sintetiza grupos quando `reservasGrupos` não veio da API. */
export function buildReservasGrupos(exc: Excursao): ReservaGrupoExcursao[] {
  if (exc.reservasGrupos && exc.reservasGrupos.length > 0) return exc.reservasGrupos;
  const inscritos = Number(exc.inscritos) || 0;
  const valorPorPessoa = Number(exc.valorPorPessoa) || 0;
  const totalFallback =
    exc.arrecadadoPorGrupo != null && Number.isFinite(exc.arrecadadoPorGrupo)
      ? exc.arrecadadoPorGrupo
      : valorPorPessoa * inscritos;
  const ids = exc.clienteIds?.filter(Boolean) ?? [];
  if (ids.length === 0) {
    return [
      {
        quantidadePessoas: Math.max(1, inscritos),
        valorTotalVenda: totalFallback,
      },
    ];
  }
  const base = Math.floor(inscritos / ids.length);
  const rest = inscritos - base * ids.length;
  return ids.map((email, i) => {
    const cad = findClienteByEmail(email);
    const pax = base + (i < rest ? 1 : 0);
    const share =
      ids.length === 1 ? totalFallback : Math.round((totalFallback * pax) / Math.max(1, inscritos));
    return {
      responsavelEmail: email,
      responsavelNome: cad ? [cad.nome, cad.sobrenome].filter(Boolean).join(' ').trim() : undefined,
      telefone: cad?.telefone,
      quantidadePessoas: Math.max(0, pax),
      valorTotalVenda: share,
    };
  });
}

export function whatsappHref(telefone: string | undefined): string {
  const raw = (telefone ?? '').replace(/\D/g, '');
  if (!raw) return '#';
  const num = raw.startsWith('55') || raw.length > 11 ? raw : `55${raw}`;
  return `https://wa.me/${num}`;
}

export function motivoCancelamentoLabel(m: MotivoCancelamentoExcursao | undefined): string {
  switch (m) {
    case 'meteorologia':
      return 'Condições meteorológicas';
    case 'quorum_insuficiente':
      return 'Quórum insuficiente';
    case 'outro':
      return 'Outro';
    default:
      return '—';
  }
}

export function computeFinanceiroExcursao(exc: Excursao) {
  const grupos = buildReservasGrupos(exc);
  const valorPorPessoa = Number(exc.valorPorPessoa) || 0;
  const totalReservas = grupos.reduce((s, g) => s + (g.valorTotalVenda ?? 0), 0);
  const total =
    totalReservas > 0
      ? totalReservas
      : exc.arrecadadoPorGrupo != null && Number.isFinite(exc.arrecadadoPorGrupo)
        ? exc.arrecadadoPorGrupo
        : valorPorPessoa * (Number(exc.inscritos) || 0);
  const inscritos = Number(exc.inscritos) || 0;
  const valorGuiaUnit = parseValorGuiaPorInscrito(exc);
  const pagamentoGuia = valorGuiaUnit * inscritos;
  const lucro = Math.max(0, total - pagamentoGuia);
  return { grupos, valorPorPessoa, total, pagamentoGuia, lucro };
}

export function buildClienteLines(
  grupos: ReservaGrupoExcursao[],
  lookup: (email: string) => ClienteRegistro | undefined
): string {
  const lines: string[] = [];
  for (const g of grupos) {
    const cad = g.responsavelEmail ? lookup(g.responsavelEmail) : undefined;
    const nome =
      (g.responsavelNome?.trim() || [cad?.nome, cad?.sobrenome].filter(Boolean).join(' ').trim() || 'Cliente') || 'Cliente';
    const tel = (g.telefone?.trim() || cad?.telefone?.trim() || '').trim();
    const pax = g.quantidadePessoas ?? 0;
    if (tel) {
      lines.push(`${nome} (${pax}) - ${tel}`);
    } else {
      lines.push(`${nome} (${pax})`);
    }
  }
  return lines.join('\n');
}

export function buildValorReservasLines(grupos: ReservaGrupoExcursao[]): string {
  if (!grupos.length) return formatBRL(0);
  return grupos.map((g) => formatBRL(g.valorTotalVenda ?? 0)).join('\n');
}
